<template>
	<!-- DEV: to change the dropdown orientation when opened add is-upward class to the dropdown div -->
	<div
		:style="{ width: dropdownWidth }"
		:class="[
			'dropdown',
			isArrowUp ? 'is-upward' : dispatchDropUp ? 'is-upward' : '',
			hasError ? 'has-error' : '',
			disabled ? 'is-disabled' : ''
		]"
		:id="uid"
		:variant="variant"
		ref="dropdown"
	>
		<Label v-if="label" :labelText="label" />
		<div
			class="dropdown__input-container"
			ref="dropdownPosRef"
			tabindex="0"
		>
			<div class="dropdown__selected" @click="displayMenu = !displayMenu">
				{{ valueText || placeholder }}
			</div>
			<div class="dropdown__trailing-container">
				<div v-if="showClearSelectedButton && notDefaultValue">
					<Button
						className="btn--icon-only"
						:btnClick="clearValue"
						:iconBeforeLabel="true"
						:iconClassName="'icon-close'"
						:id="`${uid}_btn`"
						:variant="variant"
					/>
				</div>
				<div class="dropdown__trailing-item" v-show="showIErrorIcon">
					<div class="dropdown__icon dropdown__icon--error">
						<i class="icon icon-error-solid"></i>
					</div>
				</div>
				<div
					class="dropdown__trailing-item"
					@click="displayMenu = !displayMenu"
				>
					<div class="dropdown__icon dropdown__icon--arrow">
						<i
							:class="[
								'icon',
								menuOnTop ? 'icon-caret_up' : 'icon-caret_down'
							]"
						></i>
					</div>
				</div>
			</div>
		</div>

		<DropdownListV2
			v-show="displayMenu"
			:autosuggestion="autosuggestion"
			:id="`${uid}_ddl`"
			:options="options"
			:value="value"
			@change="updateValue"
			:moreToLoad="moreToLoad"
			v-on="$listeners"
			ref="dropdownListRef"
			:showSearchOptions="showSearchOptions"
		/>
		<div class="dropdown__text dropdown__text--error" v-if="hasError">
			{{ errorText || defaultErrorText }}
		</div>
		<HelpFeedback
			class="dropdown__text"
			:helpText="helpText"
			v-if="helpText"
			data-test="helpFeedback"
		/>

		<PopperPosition
			trackedRefName="dropdownPosRef"
			popoverRefName="dropdownListRef"
			placement="bottom-start"
			:popperVisible="displayMenu"
			strategy="fixed"
			:altBoundary="altBoundary"
			@popperMinWidthChanged="setpopperMinWidth"
			@positionUpdated="updateIconOrientation"
		/>
	</div>
</template>
<script>
import Label from '@/components/forms/label/label';
import DropdownListV2 from '@/components/forms/dropdown/dropdown-list-v2';
import Button from '@/components/forms/button/button';
import PopperPosition from '@/components/visual/popper-position';
import HelpFeedback from '@/components/forms/help-feedback/help-feedback';

// mixins
import DocumentClick from '@/mixins/document-click';
import popperPositionMixin from '@/mixins/popper-position-mixin';

export default {
	name: 'DropdownV2',
	components: {
		Label,
		DropdownListV2,
		Button,
		PopperPosition,
		HelpFeedback
	},
	mixins: [DocumentClick('closeMenu'), popperPositionMixin],
	props: {
		value: [String, Number, Boolean],
		defaultValue: {
			type: [String, Number, Boolean],
			default: undefined
		},
		options: {
			type: Array,
			required: true
		},
		width: {
			type: Number,
			default: undefined
		},
		isArrowUp: {
			type: Boolean,
			default: false
		},
		showIErrorIcon: {
			type: Boolean,
			default: true
		},
		altBoundary: {
			type: String,
			default: undefined
		},
		helpText: String,
		label: String,
		showClearSelectedButton: {
			type: Boolean,
			default: true
		},
		autosuggestion: {
			type: Boolean,
			default: false
		},
		placeholder: String,
		moreToLoad: Boolean,
		timeout: Number,
		dropdownWidth: {
			type: String,
			default: '' // Default value for width
		},
		showSearchOptions: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			displayMenu: false,
			menuOnTop: false,
			popperMinWidth: 'auto',
			dispatchDropUp: null
		};
	},
	computed: {
		valueText() {
			return this.options.find(option => option.value === this.value)
				?.text;
		},
		hasError() {
			// TODO
			return false;
		},
		notDefaultValue() {
			return this.value !== this.defaultValue && this.value;
		}
	},
	methods: {
		closeMenu() {
			this.displayMenu = false;
		},
		updateValue(value) {
			this.displayMenu = false;
			this.$emit('input', value);
			this.$emit('change', value);
			this.$emit('clean-search');
		},
		clearValue() {
			if (this.defaultValue || typeof this.defaultValue === 'boolean') {
				this.$emit('input', this.defaultValue);
				this.$emit('change', this.defaultValue);
				return;
			}
			this.$refs.dropdownListRef.searchQuery = '';
			this.$emit('input', null);
			this.$emit('change', null);
			this.$emit('clean-search');
		},
		stopLoading() {
			this.$nextTick(() => {
				if (this.$refs.dropdownListRef) {
					this.$refs.dropdownListRef.loadingMore = false;
					this.$refs.dropdownListRef.loadingSearch = false;
				}
			});
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * Dropdown styles
    * Can use SCSS variables = TRUE
*/

@import '@/scss/5-objects/_form.scss';

.dropdown {
	position: relative;
	margin: 0;
	font-size: $input-font-size;

	&__label {
		display: block;
		font-size: $label-font-size;
		font-weight: $label-font-weight;
		color: inherit;
		margin-bottom: $gutter-sm;
	}

	&__input-container {
		position: relative;
		@include textfield;
		display: flex;

		&:focus {
			-o-outline: $focus-outline;
			-moz-outline: $focus-outline;
			outline: $focus-outline;
			outline-offset: $focus-offset;
		}
	}

	&__selected {
		appearance: none;
		outline: none;
		border: none;
		@include input-height;
		padding: $input-padding-y $input-padding-x;
		cursor: pointer;
		font-size: $input-font-size;
		text-overflow: ellipsis;
		background-color: transparent;
		width: 100%;
		z-index: 1;
		white-space: nowrap;
		overflow: hidden;
	}

	&__trailing-container {
		display: flex;
		align-items: center;
		font-size: $input-font-size + 2;
		z-index: 0;
	}

	&__icon {
		display: flex;
		padding-right: $input-padding-x;
		cursor: pointer;

		&--arrow {
			font-size: 8px;
		}
	}

	&__icon--error {
		display: none;
	}

	select {
		option {
			&:checked {
				background-color: $neutral-4;
			}

			&:hover,
			&:active {
				background-color: lighten($neutral-4, 5%);
			}
		}

		+ .dropdown__trailing-container {
			position: absolute;
			right: $input-padding-x;
			top: $input-padding-y;

			.dropdown__icon--arrow {
				padding-right: 0;
				color: initial;
			}
		}
	}

	&.is-upward {
		.dropdown__icon--arrow {
			.icon {
				transform: rotate(180deg);
			}
		}
		.dropdown__list-container {
			bottom: 100%;
			top: initial;
		}
	}
}

/*------------------------------------*\
  #ERROR
\*------------------------------------*/

.has-error {
	.dropdown__input-container {
		border-color: $ui-negative;

		&:focus {
			outline-color: $ui-negative;
		}
	}

	.dropdown__icon--error,
	.dropdown__text--error {
		display: block;
		color: $ui-negative;
	}

	.dropdown__text--error {
		font-size: $label-font-size - 2;
		margin-top: $gutter-sm;
	}
}
</style>
